import { defaultTargetGraphSettings, measurementMetrics } from 'boot/globals';
import { assign, chain, cloneDeep, concat, filter, find, get, isEmpty, isFunction, isNumber, round } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { AGGREGATORS, CHART_TYPE } from 'src/constants/chart';
import { TARGET_DATA_TYPE_DEFINITIONS } from 'src/constants/targets';
import DateRanges from 'src/mixins/DateRanges';
import Targets from 'src/mixins/Targets';
import Time from 'src/mixins/Time';
import Toolbox from 'src/mixins/Toolbox';
import { assessments } from 'src/store/assessments';
import { auth } from 'src/store/auth';
import { programBehaviourData } from 'src/store/programBehaviourData';
import { programs } from 'src/store/programs';

import DataSourceManager from './Insights/dataSources';

export default {
  mixins: [Targets, Toolbox, DateRanges, Time],

  data() {
    return {
      dataSources: {},
      dataSourcePresets: [],

      userNamesFromData: [],

      dateFormats: [
        {
          value: 'MMMDYY',
          luxon: 'MMM d, yy',
          amChart: 'MMM d, yy',
          label: 'MMM D, YY (e.g. Dec 1, 25)',
        },
        {
          value: 'MMMDYYYY',
          luxon: 'MMM d, yyyy',
          amChart: 'MMM d, yyyy',
          label: 'MMM D, YYYY (e.g. Dec 1, 2025)',
        },
        {
          value: 'MMDDYYYY',
          luxon: 'MM/dd/yyyy',
          amChart: 'MM/DD/yyyy',
          label: 'MM/DD/YYYY (e.g. 12/31/2025)',
        },
        {
          value: 'MMDDYY',
          luxon: 'MM/dd/yy',
          amChart: 'MM/DD/yy',
          label: 'MM/DD/YY (e.g. 12/31/25)',
        },
        {
          value: 'DDMMYYYY',
          luxon: 'dd-MM-yyyy',
          amChart: 'DD-MM-yyyy',
          label: 'DD-MM-YYYY (e.g. 31-12-2025)',
        },
        {
          value: 'DDMMYY',
          luxon: 'dd-MM-yy',
          amChart: 'DD-MM-yy',
          label: 'DD-MM-YY (e.g. 31-12-25)',
        },
        {
          value: 'YYYYMMDD',
          luxon: 'yyyy-MM-dd',
          amChart: 'YYYY-MM-DD',
          label: 'YYYY-MM-DD (e.g. 2025-12-31)',
        },
        {
          value: 'MMMM D, YYYY',
          luxon: 'MMMM d, yyyy',
          amChart: 'MMMM d, yyyy',
          label: 'MMMM D, YYYY (e.g. December 1, 2025)',
        },
      ],
    };
  },

  created() {
    /**
     * Should be defined in created() so that it won't be a reactive object as we don't need that
     *
     * @depricated Use composables/insights/registry.js instead
     */
    const DSManager = new DataSourceManager(this);
    this.dSources = DSManager.getAvailableDataSources();

    this.chartTypes = {
      [CHART_TYPE.LINE]: {
        icon: 'show_chart',
        disableGroupBy: false,
      },
      [CHART_TYPE.COLUMN]: {
        icon: 'bar_chart',
        disableGroupBy: false,
      },
      [CHART_TYPE.STACKED]: {
        icon: 'stacked_bar_chart',
        disableGroupBy: false,
      },
      [CHART_TYPE.LOGARITHMIC]: {
        icon: 'trending_up',
        disableGroupBy: false,
      },
      [CHART_TYPE.SCATTERCHART]: {
        icon: 'scatter_plot',
        disableGroupBy: false,
      },
      [CHART_TYPE.SCATTERPLOT]: {
        icon: 'fas fa-fire',
        disableGroupBy: true,
      },
    };
  },

  computed: {
    ...mapState(auth, ['currentClient']),
    ...mapState(assessments, ['assessmentTypeFieldGroups', 'assessmentTypeFields']),
    ...mapState(programs, ['programs']),
    ...mapState(programBehaviourData, ['programBehaviourGroups', 'programBehaviourGroupItems']),
  },

  methods: {
    ...mapActions(assessments, ['getAssessmentFormFields']),

    clearDataSources() {
      this.dataSources = {};
    }, // clearDataSources()

    // @deprecated use resolveDateRange() from the useDateRanges composable instead
    applyDateRangePreset(dateRange) {
      const returnDateRange = {
        ...find(this.periodPresets, { id: dateRange.preset }),
        preset: dateRange.preset,
      };
      if (dateRange.preset === 'custom') {
        returnDateRange.fromTimestamp = dateRange.fromTimestamp.toDate();
        returnDateRange.toTimestamp = dateRange.toTimestamp.toDate();
        returnDateRange.label = dateRange.label;
      }
      if (dateRange.periodDimension) {
        returnDateRange.periodDimension = dateRange.periodDimension;
      }
      return returnDateRange;
    }, // applyDateRangePreset()

    /**
     * Returns label for the a preset time period
     * @param {*} value
     */
    getSelectedPeriodLabel(value, hasPeriodDimension = false, noPeriodFilterLabel = '') {
      if (hasPeriodDimension && get(value, 'periodDimension', null) === null) {
        return noPeriodFilterLabel;
      }
      return (
        get(value, 'label')
        || chain(this.periodPresets)
          .find({ id: get(value, 'preset') })
          .get('label')
          .defaultTo(
            get(value, 'preset') === 'inherit'
              ? this.$t('insights.chartConfigurator.periodPresetInherit')
              : this.$t('insights.chartConfigurator.periodPresetCustom'),
          )
          .value()
      );
    }, // getSelectedPeriodLabel()

    getPhaseLineScopeFilter(scope, program = null, target = null) {
      const scopeFilter = { scope };
      if (program) {
        scopeFilter.program = program;
      }
      if (target) {
        scopeFilter.target = target;
      }
      return scopeFilter;
    }, // getPhaseLineScopeFilter()

    /**
     * Returns columns for display in data table and export
     *
     */
    async getTableColumns(tableColumns, series, dataSeriesSettings, getExportColumns, tableData) {
      let columns = tableColumns
        .filter((column) => {
          const visibilityKey = getExportColumns ? 'visibilityExport' : 'visibility';
          return (column[visibilityKey] || []).includes(dataSeriesSettings.xAxisAggregateBy);
        })
        .map((column) => {
          if (column.name === 'xAxis') {
            const label = dataSeriesSettings.xAxisType === 'date'
              ? this.$t('insights.chartConfigurator.columnsDate')
              : chain(this.dSources)
                .find({ id: dataSeriesSettings.dataSource })
                .get('xAxes')
                .find({ id: dataSeriesSettings.xAxis })
                .get('label')
                .value();

            const isDateAxisType = dataSeriesSettings.xAxisType === 'date';
            const format = isDateAxisType ? (val) => (val ? this.getLocaleDateString(val) : '') : (val) => val;

            return {
              ...column,
              field: dataSeriesSettings.xAxisCategoryLabel || dataSeriesSettings.xAxisDataProperty,
              label,
              format,
            };
          }
          return column;
        });

      const expandableColumns = [];
      const nonExpandableColumns = [];

      columns.forEach((col) => {
        if (isFunction(col?.expand)) {
          expandableColumns.push(col);
        } else {
          nonExpandableColumns.push(col);
        }
      });

      if (dataSeriesSettings.xAxisAggregateBy === 'assessmentId') {
        columns = [...expandableColumns, ...nonExpandableColumns];
      } else {
        columns = [...nonExpandableColumns, ...expandableColumns];
      }

      // Function made to prevent lint errors
      const getExpandedCol = (col) => col.expand(tableData).map((expandedItem) => ({ ...col, ...expandedItem }));
      if (expandableColumns.length > 0) {
        columns = columns.flatMap((col) => (isFunction(col.expand) ? getExpandedCol(col) : col));
      }
      const valueColumn = await this.getColumnsBySeries(series);
      if (!isEmpty(valueColumn)) {
        columns = concat(columns, valueColumn);
      }

      return columns;
    }, // getTableColumns()

    async getColumnsBySeries(series) {
      return series.map((seriesItem) => {
        if (get(seriesItem, 'hideColumnInTable', false)) {
          return {};
        }

        const columnObject = {
          name: seriesItem.seriesColumn,
          field: seriesItem.seriesColumn,
          label: seriesItem.label,
          align: 'left',
          sortable: true,
        };

        const formattingFields = {
          number: () => {
            const numberDecimals = seriesItem.axisNumberDecimals || 0;

            return {
              format: (val) => (isNumber(val) ? round(val, numberDecimals) : ''),
              aggrFormat: (val, aggregator) => {
                const precision = [AGGREGATORS.AVERAGE, AGGREGATORS.WEIGHTED_AVERAGE].includes(aggregator) ? 2 : 0;
                return isNumber(val) ? round(val, precision) : '';
              },
            };
          },
          percentage: () => ({
            format: (val) => (isNumber(val) ? `${round(val * 100, 0)}%` : ''),
          }),
          frequencyMinute: () => {
            const getText = (value) => `${Math.round(value)} ${this.$t('insights.insightsTable.PerMinuteLabel')}`;
            return {
              format: (val) => (val === null ? '' : getText(val)),
            };
          },
          frequencyHour: () => {
            const getText = (value) => `${Math.round(value)} ${this.$t('insights.insightsTable.PerHourLabel')}`;
            return {
              format: (val) => (val === null ? '' : getText(val)),
            };
          },
          frequencyDay: () => {
            const getText = (value) => `${Math.round(value)} ${this.$t('insights.insightsTable.PerDayLabel')}`;
            return {
              format: (val) => (val === null ? '' : getText(val)),
            };
          },
          duration: () => ({
            format: (val) => (isNumber(val) ? this.formatDuration(val) : ''),
          }),
        };

        if (!formattingFields[seriesItem.axis]) {
          return {};
        }
        assign(columnObject, formattingFields[seriesItem.axis]());

        return columnObject;
      });
    }, // getColumnsBySeries()

    // @deprecated Migrated to resolveProgramTargetProgressSettings in useProgramGraphs for Composition API refactor
    getDefaultTargetDataSeriesSettings(target, programTargets, program = null, viewType = 'default') {
      const defaultGraphSettings = cloneDeep(
        get(defaultTargetGraphSettings, viewType, {
          dataSource: 'dsSessionData',
          dataSeriesPreset: viewType === 'spAverageRating' ? 'spAverageRating' : 'spSuccessRate',
          xAxis: 'xaDateDaily',
        }),
      );
      if (program && viewType === 'default') {
        const primaryMetric = this.getTargetMetric(target, program);
        if (primaryMetric) {
          const dataSeriesPreset = measurementMetrics.find((m) => m.id === primaryMetric)?.dataSeriesPreset;
          if (dataSeriesPreset) {
            defaultGraphSettings.dataSeriesPreset = dataSeriesPreset;
          }
        }
      }

      const seriesPresets = chain(this.dSources)
        .find({ id: defaultGraphSettings.dataSource })
        .get('seriesPresets')
        .find({ id: defaultGraphSettings.dataSeriesPreset })
        .omit('id')
        .value();
      const xAxis = chain(this.dSources)
        .find({ id: defaultGraphSettings.dataSource })
        .get('xAxes')
        .find({ id: defaultGraphSettings.xAxis })
        .omit('id')
        .value();
      const targetHasSubTargets = chain(TARGET_DATA_TYPE_DEFINITIONS)
        .find({ value: target.dataType })
        .get('hasSubTargets', false)
        .value();
      let postloadFilter;
      const parentTarget = get(target, 'parentTarget', null);
      const parentTargetIds = this.getParentTargetsIds(target, programTargets);
      if (targetHasSubTargets && parentTargetIds.length) {
        postloadFilter = {
          programs: {
            programId: [program ? program.id : target.programId],
            parentTargetId: parentTargetIds,
          },
        };
      } else {
        postloadFilter = {
          programs: {
            programId: [program ? program.id : target.programId],
            targetId: [get(target, 'id', null)],
          },
        };
      }

      const phaseLines = filter(
        [
          {
            scope: parentTarget ? 'subTarget' : 'target',
            program: program ? program.id : target.programId,
            target: target.id,
            isSelected: true,
          },
        ],
        'program',
      );
      return {
        postloadFilter,
        preloadFilter: null,
        phaseLines,

        ...xAxis,
        ...seriesPresets,
        ...defaultGraphSettings,
      };
    }, // getDefaultTargetDataSeriesSettings()

    getParentTargetsIds(target, programTargets) {
      const parentTargetsIds = [];
      const subTargets = filter(programTargets, { parentTarget: target.id });

      if (subTargets.length) {
        parentTargetsIds.push(target.id);

        subTargets.forEach((subTarget) => {
          const targetHasSubTargets = filter(programTargets, { parentTarget: subTarget.id }).length;

          if (targetHasSubTargets) {
            parentTargetsIds.push(...this.getParentTargetsIds(subTarget, programTargets));
          }
        });
      }

      return parentTargetsIds;
    }, // getParentTargetsIds()
  },
};
/*
Prevent dobbelt-load
*/
